import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import styled from 'styled-components';
import {
  Currency,
  DateTime,
  ExportDropdown,
  Hours,
  Percent,
  PeriodFilter,
  SingleSelect,
  Widget,
  WorkspaceCurrencySelect,
} from '~/components';
import {
  CurrencyFilter,
  DayPickerFilter,
  FilterButton,
  MemberFiltersBar,
  MemberFiltersGroup,
  PlaceholderFiltersBar,
  PlaceholderFiltersGroup,
  ResourceTypeFilter,
  SingleSelectFilter,
} from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useCurrencyFormat,
  useDateTimeFormat,
  useDocumentTitle,
  useFeatures,
  useMemberFilters,
  usePlaceholderFilters,
  useSearchParams,
  useSearchParamsConfig,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, intervalOptions, mimeTypes, sortAlphabetically } from '~/utils';
import IconButton from '../../../../components/IconButton';
import ExportDialogAsync from '../components/ExportDialogAsync';
import List from '../components/List';
import ListTooltip from '../components/ListTooltip';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';
import { intervals, intervalsByScope } from './intervals';

const TimelineContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  width: 100%;
  height: 1.5rem;
  background: ${colors.white};
`;

const TimelineBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
`;

const TimelineGutter = styled.div`
  flex: 1;
  min-width: 16rem;
  position: sticky;
  left: -1rem;
  background: linear-gradient(to right, ${colors.white} 90%, transparent 100%);
`;

function Timeline({ periods, unit, actualsThrough }) {
  const groupedPeriods = useMemo(() => {
    const u = unit === 'week' ? 'isoWeek' : unit;
    const today = moment(actualsThrough);
    return _.groupBy(periods, (p) => {
      const period = moment(p);
      const endOfUnit = period.clone().endOf(u);
      if (period.isBefore(today, u) || endOfUnit.isSame(today, 'day')) {
        return 'actuals';
      } else if (period.isSame(today, u)) {
        return 'in_progress';
      } else {
        return 'plan';
      }
    });
  }, [periods, unit, actualsThrough]);

  return (
    <TimelineContainer>
      <TimelineGutter />

      {_.map(groupedPeriods, (periods, key) => {
        let style;
        let label;
        const width = `${periods.length * 10}rem`;
        switch (key) {
          case 'actuals':
            style = { color: colors.white, backgroundColor: colors.grey55, width };
            label = 'Actuals';
            break;

          case 'in_progress':
            style = { color: colors.white, backgroundColor: colors.grey40, width };
            label = 'Actuals/Plan';
            break;

          case 'plan':
            style = { color: colors.black, backgroundColor: colors.grey25, width };
            label = 'Plan';
            break;
        }

        return (
          <TimelineBar key={key} style={style}>
            {label}
          </TimelineBar>
        );
      })}

      <div style={{ width: '10rem' }} />
    </TimelineContainer>
  );
}

export default function PerformanceForecastByResourceByTimeUnit({ report }) {
  useDocumentTitle(report.name);

  const { workspace } = useWorkspace();
  const api = useApi();

  const features = useFeatures();

  const metricOptions = useMemo(() => {
    const options = {
      services_revenue: 'Services Revenue',
      services_profit: 'Services Gross Profit',
      labor_cost: 'Services Cost',
      services_margin: 'Services Gross Margin',
      total_hours: 'Total Hours',
    };

    if (features.utilization) {
      Object.assign(options, {
        billable_utilization: 'Billable Utilization',
        client_utilization: 'Client Utilization',
        productive_utilization: 'Productive Utilization',
        total_utilization: 'Total Utilization',
      });
    }

    return options;
  }, [features.utilization]);

  const searchParamsConfig = useSearchParamsConfig();

  const [query, setQuery] = useState({
    report: null,
    status: 'loading',
    loading: { table: false },
  });

  const memberFilters = useMemberFilters();
  const placeholderFilters = usePlaceholderFilters();

  const [params, setParams] = useState({
    period: null,
    unit: 'month',
    currency: workspace.currency,
    metric: 'services_revenue',
    actualsThrough: null,
    resourceTypeId: null,

    ...memberFilters.filters,
    ...placeholderFilters.filters,
  });

  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.periodByUnit,
          default: {
            start: moment().subtract(2, 'months').startOf('month').format(dateFormats.isoDate),
            end: moment().add(2, 'months').endOf('month').format(dateFormats.isoDate),
            key: intervalOptions.custom.key,
            unit: 'month',
          },
          intervals: (unit) => intervalsByScope[unit],
          defaults: {
            day: intervals.next_30_days,
            week: intervals.next_12_weeks,
            month: intervals.next_6_months,
          },
        },
        unit: { default: 'month', valid: ['day', 'week', 'month'] },
        currency: searchParamsConfig.currency,
        metric: { default: 'services_revenue', valid: _.keys(metricOptions) },
        actualsThrough: {
          default: moment().format(dateFormats.isoDate),
          deserialize: (value) => {
            if (!moment(value, true).isValid()) {
              value = null;
            }
            return value;
          },
        },
        resourceTypeId: searchParamsConfig.resourceTypeId,

        ...memberFilters.searchParamsConfig,
        ...placeholderFilters.searchParamsConfig,
      }),
      [searchParamsConfig, memberFilters.searchParamsConfig, placeholderFilters.searchParamsConfig, metricOptions],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      unit: params.unit,
      currency: params.currency ?? undefined,
      actualsThrough: params.actualsThrough ?? undefined,
      resourceTypeId: params.resourceTypeId ?? undefined,

      ...memberFilters.mapUrlSearchParams(params),
      ...placeholderFilters.mapUrlSearchParams(params),
    }),
    [params, memberFilters, placeholderFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  // Updated to use new API pattern
  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({
      ...state,
      data,
      status: 'ready',
      loading: { table: false },
    }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideFilters();
  };

  const [settingsVisible, setSettingsVisible] = useState(false);
  const showSettings = () => setSettingsVisible(true);
  const hideSettings = () => setSettingsVisible(false);
  const handleApplySettings = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideSettings();
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />

          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="cog" onClick={showSettings} />

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <PeriodFilter
          clearable={false}
          scope={params.unit}
          intervals={intervalsByScope[params.unit]}
          placeholder="Date Range"
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        <DayPickerFilter
          placeholder="Actuals Through"
          value={params.actualsThrough}
          onChange={({ target: { value } }) => handleApplyFilters({ actualsThrough: value })}
          icon="gear"
        />

        <UnitFilter
          value={params.unit}
          onChange={({ target: { value } }) =>
            handleApplyFilters({
              unit: value,
              period: {
                day: intervals.next_30_days,
                week: intervals.next_12_weeks,
                month: intervals.next_6_months,
              }[value],
            })
          }
        />

        {features.multicurrency && (
          <CurrencyFilter
            value={params.currency}
            onChange={({ target: { value } }) => handleApplySettings({ currency: value })}
          />
        )}

        <SingleSelectFilter
          icon="gear"
          placeholder="Metric"
          renderValue={(value) => value.name}
          value={params.metric}
          options={_.map(metricOptions, (value, key) => ({ id: key, name: value }))}
          onChange={({ target: { value } }) => handleApplySettings({ metric: value })}
          clearable={false}
        />

        <ResourceTypeFilter
          value={params.resourceTypeId}
          onChange={({ target: { value } }) => handleApplySettings({ resourceTypeId: value })}
        />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />

        <PlaceholderFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} />
                <Settings
                  values={params}
                  isOpen={settingsVisible}
                  onApply={handleApplySettings}
                  onClose={hideSettings}
                />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params }) {
  const report = query.data;

  const { resourceTypesById, resourcesById, performancePeriods, periods } = useMemo(() => {
    const resourceTypesById = {
      member: { id: 'member', name: 'Members' },
      placeholder: { id: 'placeholder', name: 'Placeholders' },
    };

    const resourcesById = _.keyBy(report.resources, 'id');

    const performancePeriods = sortAlphabetically(report.periods, [
      'resourceTypeId',
      (p) => resourcesById[p.resourceId]?.name,
    ]).reduce(
      (a, v) => {
        a.total = a.total || {
          capacity: 0,
          billableHours: 0,
          nonBillableHours: 0,
          internalHours: 0,
          timeOffHours: 0,
          totalClientHours: 0,
          totalProductiveHours: 0,
          totalProjectHours: 0,
          totalHours: 0,
          servicesRevenue: 0,
          laborCost: 0,
          servicesProfit: 0,
          get servicesMargin() {
            return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : 0;
          },
          get billableUtilization() {
            return this.capacity ? this.billableHours / this.capacity : null;
          },
          get clientUtilization() {
            return this.capacity ? this.totalClientHours / this.capacity : null;
          },
          get productiveUtilization() {
            return this.capacity ? this.totalProductiveHours / this.capacity : null;
          },
          get totalUtilization() {
            return this.capacity ? this.totalProjectHours / this.capacity : null;
          },
        };

        a.total.capacity += v.capacity;
        a.total.billableHours += v.billableHours;
        a.total.nonBillableHours += v.nonBillableHours;
        a.total.internalHours += v.internalHours;
        a.total.timeOffHours += v.timeOffHours;
        a.total.totalClientHours += v.totalClientHours;
        a.total.totalProductiveHours += v.totalProductiveHours;
        a.total.totalProjectHours += v.totalProjectHours;
        a.total.totalHours += v.totalHours;
        a.total.servicesRevenue += v.servicesRevenue;
        a.total.laborCost += v.laborCost;
        a.total.servicesProfit += v.servicesProfit;

        const resourceType = resourceTypesById[v.resourceTypeId];
        a.resourceTypes[resourceType.id] = a.resourceTypes[resourceType.id] || {
          total: {
            resourceTypeId: v.resourceTypeId,
            capacity: 0,
            billableHours: 0,
            nonBillableHours: 0,
            internalHours: 0,
            timeOffHours: 0,
            totalClientHours: 0,
            totalProductiveHours: 0,
            totalProjectHours: 0,
            totalHours: 0,
            servicesRevenue: 0,
            laborCost: 0,
            servicesProfit: 0,
            get servicesMargin() {
              return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : null;
            },
            get billableUtilization() {
              return this.capacity ? this.billableHours / this.capacity : null;
            },
            get clientUtilization() {
              return this.capacity ? this.totalClientHours / this.capacity : null;
            },
            get productiveUtilization() {
              return this.capacity ? this.totalProductiveHours / this.capacity : null;
            },
            get totalUtilization() {
              return this.capacity ? this.totalProjectHours / this.capacity : null;
            },
          },
          periods: {},
          resources: {},
        };
        // Resource Overall Totals
        a.resourceTypes[v.resourceTypeId].total.capacity += v.capacity;
        a.resourceTypes[v.resourceTypeId].total.billableHours += v.billableHours;
        a.resourceTypes[v.resourceTypeId].total.nonBillableHours += v.nonBillableHours;
        a.resourceTypes[v.resourceTypeId].total.internalHours += v.internalHours;
        a.resourceTypes[v.resourceTypeId].total.timeOffHours += v.timeOffHours;
        a.resourceTypes[v.resourceTypeId].total.totalClientHours += v.totalClientHours;
        a.resourceTypes[v.resourceTypeId].total.totalProductiveHours += v.totalProductiveHours;
        a.resourceTypes[v.resourceTypeId].total.totalProjectHours += v.totalProjectHours;
        a.resourceTypes[v.resourceTypeId].total.totalHours += v.totalHours;
        a.resourceTypes[v.resourceTypeId].total.servicesRevenue += v.servicesRevenue;
        a.resourceTypes[v.resourceTypeId].total.laborCost += v.laborCost;
        a.resourceTypes[v.resourceTypeId].total.servicesProfit += v.servicesProfit;

        // Resource Type Periods
        if (!a.resourceTypes[v.resourceTypeId].periods[v.start]) {
          a.resourceTypes[v.resourceTypeId].periods[v.start] = {
            resourceTypeId: v.resourceTypeId,
            start: v.start,
            capacity: 0,
            billableHours: 0,
            nonBillableHours: 0,
            internalHours: 0,
            timeOffHours: 0,
            totalClientHours: 0,
            totalProductiveHours: 0,
            totalProjectHours: 0,
            totalHours: 0,
            servicesRevenue: 0,
            laborCost: 0,
            servicesProfit: 0,
            get servicesMargin() {
              return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : null;
            },
            get billableUtilization() {
              return this.capacity ? this.billableHours / this.capacity : null;
            },
            get clientUtilization() {
              return this.capacity ? this.totalClientHours / this.capacity : null;
            },
            get productiveUtilization() {
              return this.capacity ? this.totalProductiveHours / this.capacity : null;
            },
            get totalUtilization() {
              return this.capacity ? this.totalProjectHours / this.capacity : null;
            },
          };
        }
        a.resourceTypes[v.resourceTypeId].periods[v.start].capacity += v.capacity;
        a.resourceTypes[v.resourceTypeId].periods[v.start].billableHours += v.billableHours;
        a.resourceTypes[v.resourceTypeId].periods[v.start].nonBillableHours += v.nonBillableHours;
        a.resourceTypes[v.resourceTypeId].periods[v.start].internalHours += v.internalHours;
        a.resourceTypes[v.resourceTypeId].periods[v.start].timeOffHours += v.timeOffHours;
        a.resourceTypes[v.resourceTypeId].periods[v.start].totalClientHours += v.totalClientHours;
        a.resourceTypes[v.resourceTypeId].periods[v.start].totalProductiveHours += v.totalProductiveHours;
        a.resourceTypes[v.resourceTypeId].periods[v.start].totalProjectHours += v.totalProjectHours;
        a.resourceTypes[v.resourceTypeId].periods[v.start].totalHours += v.totalHours;
        a.resourceTypes[v.resourceTypeId].periods[v.start].servicesRevenue += v.servicesRevenue;
        a.resourceTypes[v.resourceTypeId].periods[v.start].laborCost += v.laborCost;
        a.resourceTypes[v.resourceTypeId].periods[v.start].servicesProfit += v.servicesProfit;

        a.resourceTypes[v.resourceTypeId].resources[v.resourceId] = a.resourceTypes[v.resourceTypeId].resources[
          v.resourceId
        ] || {
          periods: {},
          total: {
            resourceTypeId: v.resourceTypeId,
            capacity: 0,
            billableHours: 0,
            nonBillableHours: 0,
            internalHours: 0,
            timeOffHours: 0,
            totalClientHours: 0,
            totalProductiveHours: 0,
            totalProjectHours: 0,
            totalHours: 0,
            servicesRevenue: 0,
            laborCost: 0,
            servicesProfit: 0,
            get servicesMargin() {
              return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : 0;
            },
            get billableUtilization() {
              return this.capacity ? this.billableHours / this.capacity : null;
            },
            get clientUtilization() {
              return this.capacity ? this.totalClientHours / this.capacity : null;
            },
            get productiveUtilization() {
              return this.capacity ? this.totalProductiveHours / this.capacity : null;
            },
            get totalUtilization() {
              return this.capacity ? this.totalProjectHours / this.capacity : null;
            },
          },
        };

        // Resource Overall Totals
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.capacity += v.capacity;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.billableHours += v.billableHours;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.nonBillableHours += v.nonBillableHours;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.internalHours += v.internalHours;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.timeOffHours += v.timeOffHours;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.totalClientHours += v.totalClientHours;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.totalProductiveHours += v.totalProductiveHours;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.totalProjectHours += v.totalProjectHours;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.totalHours += v.totalHours;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.servicesRevenue += v.servicesRevenue;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.laborCost += v.laborCost;
        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].total.servicesProfit += v.servicesProfit;

        a.resourceTypes[v.resourceTypeId].resources[v.resourceId].periods[v.start] = { ...v };

        // Period Totals
        if (!a.periods[v.start]) {
          a.periods[v.start] = {
            start: v.start,
            capacity: 0,
            billableHours: 0,
            nonBillableHours: 0,
            internalHours: 0,
            timeOffHours: 0,
            totalClientHours: 0,
            totalProductiveHours: 0,
            totalProjectHours: 0,
            totalHours: 0,
            servicesRevenue: 0,
            laborCost: 0,
            servicesProfit: 0,
            get servicesMargin() {
              return this.servicesRevenue ? this.servicesProfit / this.servicesRevenue : null;
            },
            get billableUtilization() {
              return this.capacity ? this.billableHours / this.capacity : null;
            },
            get clientUtilization() {
              return this.capacity ? this.totalClientHours / this.capacity : null;
            },
            get productiveUtilization() {
              return this.capacity ? this.totalProductiveHours / this.capacity : null;
            },
            get totalUtilization() {
              return this.capacity ? this.totalProjectHours / this.capacity : null;
            },
          };
        }
        a.periods[v.start].capacity += v.capacity;
        a.periods[v.start].billableHours += v.billableHours;
        a.periods[v.start].nonBillableHours += v.nonBillableHours;
        a.periods[v.start].internalHours += v.internalHours;
        a.periods[v.start].timeOffHours += v.timeOffHours;
        a.periods[v.start].totalClientHours += v.totalClientHours;
        a.periods[v.start].totalProductiveHours += v.totalProductiveHours;
        a.periods[v.start].totalProjectHours += v.totalProjectHours;
        a.periods[v.start].totalHours += v.totalHours;
        a.periods[v.start].servicesRevenue += v.servicesRevenue;
        a.periods[v.start].laborCost += v.laborCost;
        a.periods[v.start].servicesProfit += v.servicesProfit;

        return a;
      },
      { total: null, resourceTypes: {}, periods: {} },
    );

    let start = params.period.start;
    let end = params.period.end;

    switch (params.unit) {
      case 'day':
        start = moment(start).format(dateFormats.isoDate);
        end = moment(end).format(dateFormats.isoDate);
        break;

      case 'week':
        start = moment(start).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(end).endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'month':
        start = moment(start).startOf('month').format(dateFormats.isoDate);
        end = moment(end).endOf('month').format(dateFormats.isoDate);
        break;
    }

    const periodCount = moment(end).diff(start, params.unit) + 1;
    const periods = [];
    for (let index = 0; index < periodCount; index++) {
      periods.push(moment(start).add(index, params.unit).format(dateFormats.isoDate));
    }

    return { resourceTypesById, resourcesById, performancePeriods, periods };
  }, [report, params.period.start, params.period.end, params.unit]);

  const metric = params.metric;

  return (
    <>
      <Report.Summary>
        <Chart periods={periods} records={performancePeriods} unit={params.unit} currency={report.currency} />
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header style={{ paddingTop: '1.5rem' }}>
            <Timeline periods={periods} unit={params.unit} actualsThrough={report.actualsThrough} />

            <Table.Column minWidth="16rem" sticky>
              Resource
            </Table.Column>

            {periods.map((p) => {
              return (
                <Table.Column key={p} align="right" width="10rem">
                  <DateTime value={p} />
                </Table.Column>
              );
            })}

            <Table.Column align="right" width="10rem">
              Total
            </Table.Column>
          </Table.Header>

          <Table.Body fade={query.loading.table}>
            {_.map(performancePeriods.resourceTypes, (resourceTypePerformance, resourceTypeId) => {
              const resourceType = resourceTypesById[resourceTypeId];

              return (
                <React.Fragment key={resourceTypeId}>
                  <Table.GroupRow top="4.5rem">
                    <Table.Cell>
                      {resourceType.name} ({Object.keys(resourceTypePerformance.resources).length})
                    </Table.Cell>

                    {periods.map((period) => {
                      return (
                        <Table.Cell key={period}>
                          <PerformanceCell
                            value={resourceTypePerformance.periods[period]}
                            metric={metric}
                            currency={report.currency}
                          />
                        </Table.Cell>
                      );
                    })}

                    <Table.Cell>
                      <PerformanceCell
                        value={resourceTypePerformance.total}
                        metric={metric}
                        currency={report.currency}
                      />
                    </Table.Cell>
                  </Table.GroupRow>

                  {_.map(resourceTypePerformance.resources, (resourcePerformance, resourceId) => {
                    const resource = resourcesById[resourceId];

                    return (
                      <Table.Row key={resourceId}>
                        <Table.Cell>
                          <p style={{ marginLeft: '1rem' }}>{resource.name}</p>
                        </Table.Cell>

                        {periods.map((period) => {
                          return (
                            <Table.Cell key={period}>
                              <PerformanceCell
                                value={resourcePerformance.periods[period]}
                                metric={metric}
                                currency={report.currency}
                              />
                            </Table.Cell>
                          );
                        })}

                        <Table.Cell>
                          <PerformanceCell
                            value={resourcePerformance.total}
                            metric={metric}
                            currency={report.currency}
                          />
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </React.Fragment>
              );
            })}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>

              {periods.map((period) => {
                return (
                  <Table.Cell key={period}>
                    <PerformanceCell
                      value={performancePeriods.periods[period]}
                      metric={metric}
                      currency={report.currency}
                    />
                  </Table.Cell>
                );
              })}

              <Table.Cell>
                <PerformanceCell value={performancePeriods.total} metric={metric} currency={report.currency} />
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status total={_.size(resourcesById)} label="Resource" isLoading={query.status !== 'ready'} />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <UnitFilter
        value={filters.unit}
        onChange={({ target: { value } }) =>
          handleFilter({
            unit: value,
            period: {
              day: intervals.next_30_days,
              week: intervals.next_12_weeks,
              month: intervals.next_6_months,
            }[value],
          })
        }
      />

      <PeriodFilter
        clearable={false}
        scope={filters.unit}
        intervals={intervalsByScope[filters.unit]}
        placeholder="Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ResourceTypeFilter
        value={filters.resourceTypeId}
        onChange={({ target: { value } }) => handleFilter({ resourceTypeId: value })}
      />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <PlaceholderFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

function Settings({ values, isOpen, onClose, onApply }) {
  const features = useFeatures();
  const [settings, setSettings] = useState(values);

  const handleApply = () => {
    onApply(settings);
  };

  const handleChange = (filter) => {
    setSettings({ ...settings, ...filter });
  };

  const handleCancel = () => {
    setSettings(values);
    onClose();
  };

  useEffect(() => {
    setSettings(values);
  }, [values]);

  const metricOptions = useMemo(() => {
    const options = {
      services_revenue: 'Services Revenue',
      services_profit: 'Services Gross Profit',
      labor_cost: 'Services Cost',
      services_margin: 'Services Gross Margin',
      total_hours: 'Total Hours',
    };

    if (features.utilization) {
      Object.assign(options, {
        billable_utilization: 'Billable Utilization',
        client_utilization: 'Client Utilization',
        productive_utilization: 'Productive Utilization',
        total_utilization: 'Total Utilization',
      });
    }

    return options;
  }, [features.utilization]);

  return (
    <Report.FiltersDrawer title="Settings" isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <SingleSelect
        materialPlaceholder="Metric"
        value={settings.metric}
        onChange={({ target: { value } }) => handleChange({ metric: value })}>
        {_.map(metricOptions, (value, key) => (
          <option key={key} value={key}>
            {value}
          </option>
        ))}
      </SingleSelect>

      {features.multicurrency && (
        <WorkspaceCurrencySelect
          clearable={false}
          value={settings.currency}
          onChange={({ target: { value } }) => handleChange({ currency: value })}
        />
      )}
    </Report.FiltersDrawer>
  );
}

function PerformanceCell({ value, metric, currency }) {
  return (
    <PerformanceTooltip value={value} currency={currency}>
      <div>
        <PerformanceValue value={value} metric={metric} currency={currency} />
      </div>
    </PerformanceTooltip>
  );
}

function PerformanceValue({ value, metric, currency }) {
  switch (metric) {
    case 'services_revenue':
      return <Currency value={value.servicesRevenue} currency={currency} />;

    case 'total_hours':
      return <Hours value={value.totalHours} />;

    case 'labor_cost':
      return <Currency value={value.laborCost} currency={currency} />;

    case 'services_profit':
      return <Currency value={value.servicesProfit} currency={currency} />;

    case 'services_margin':
      return <Percent value={value.servicesMargin} />;

    case 'billable_utilization':
      return <Percent value={value.billableUtilization} />;

    case 'client_utilization':
      return <Percent value={value.clientUtilization} />;

    case 'productive_utilization':
      return <Percent value={value.productiveUtilization} />;

    case 'total_utilization':
      return <Percent value={value.totalUtilization} />;

    default:
      return null;
  }
}

function PerformanceTooltip({ value, currency, children }) {
  const features = useFeatures();

  if (!value) return null;

  return (
    <ListTooltip
      message={
        <List>
          <List.Item label="Client Billable Hours">
            <Hours value={value.billableHours} />
          </List.Item>
          <List.Item label="Client Non-Billable Hours">
            <Hours value={value.nonBillableHours} />
          </List.Item>
          <List.Item label="Internal Hours">
            <Hours value={value.internalHours} />
          </List.Item>
          <List.Item label="Time Off Hours">
            <Hours value={value.timeOffHours} />
          </List.Item>
          <List.Item label="Total Hours">
            <Hours value={value.totalHours} />
          </List.Item>
          <List.Item label="Services Revenue">
            <Currency value={value.servicesRevenue} currency={currency} />
          </List.Item>
          <List.Item label="Services Cost">
            <Currency value={value.laborCost} currency={currency} />
          </List.Item>
          <List.Item label="Services Gross Profit">
            <Currency value={value.servicesProfit} currency={currency} />
          </List.Item>
          <List.Item label="Services Gross Margin">
            <Percent value={value.servicesMargin} />
          </List.Item>
          {features.utilization && value.resourceTypeId !== 'placeholder' && (
            <>
              <List.Item label="Utilization Capacity">
                <Hours value={value.capacity} />
              </List.Item>
              <List.Item label="Billable Utilization">
                <Percent value={value.billableUtilization} />
              </List.Item>
              <List.Item label="Client Utilization">
                <Percent value={value.clientUtilization} />
              </List.Item>
              <List.Item label="Productive Utilization">
                <Percent value={value.productiveUtilization} />
              </List.Item>
              <List.Item label="Total Utilization">
                <Percent value={value.totalUtilization} />
              </List.Item>
            </>
          )}
        </List>
      }>
      {children}
    </ListTooltip>
  );
}

function Chart({ periods, records, unit, currency }) {
  const dateTimeFormat = useDateTimeFormat(dateFormats.compactDate);

  const { settings, datasets } = useMemo(() => {
    const data = periods.map((period) => records.periods[period] ?? period);

    const settings = {
      day: () => ({
        labels: data.map(({ start }) => dateTimeFormat.format(start)),
      }),
      week: () => ({
        labels: data.map(({ start }) => dateTimeFormat.format(start)),
      }),
      month: () => ({
        labels: data.map(({ start }) => moment(start).format(dateFormats.monthYear)),
      }),
    }[unit]();

    const datasets = [
      {
        id: 'services_revenue',
        label: 'Services Revenue    ',
        data: data.map(({ servicesRevenue }) => servicesRevenue),
        backgroundColor: colors.primary,
      },
      {
        id: 'profit',
        label: 'Profit    ',
        data: data.map(({ servicesProfit }) => servicesProfit),
        backgroundColor: colors.primary25,
      },
    ];

    return { settings, datasets };
  }, [periods, records, unit, dateTimeFormat]);

  const currencyFormat = {
    tooltip: useCurrencyFormat({ currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }),
  };

  return (
    <Widget style={{ height: '20rem' }}>
      <Bar
        data={{
          labels: settings.labels,
          datasets: datasets,
        }}
        options={{
          maintainAspectRatio: false,

          layout: {
            padding: { top: 12 },
          },

          plugins: {
            legend: {
              onClick: null,
              labels: {
                font: { size: 12 },
                pointStyleWidth: 14,
                boxHeight: 10,
                filter: (item) => !item.text.includes('Total'),
                usePointStyle: true,
              },
            },

            tooltip: {
              filter: (item) => item.datasetIndex <= 3,
              callbacks: {
                title: () => '',
                label: (tooltip) => {
                  let label = (tooltip.dataset.label || '').trim();
                  if (label) {
                    label += ': ';
                  }
                  label += currencyFormat.tooltip.format(tooltip.parsed.y);
                  return label;
                },
              },
            },
          },

          scales: {
            x: {
              grid: { display: false },
              ticks: {
                font: {
                  size: 12,
                  weight: 'bold',
                },
                color: colors.grey40,
                minRotation: settings.rotation,
                maxRotation: settings.rotation,
              },
            },

            y: {
              display: true,
              grid: { display: true, color: colors.grey10 },
              border: { display: false },
              max: settings.max,
              ticks: { stepSize: settings.stepSize, color: colors.grey40 },
            },
          },
        }}
      />
    </Widget>
  );
}

function UnitFilter(props) {
  return (
    <SingleSelectFilter
      icon="gear"
      clearable={false}
      placeholder="Unit"
      renderValue={(value) => value.name}
      options={[
        { id: 'month', name: 'Month' },
        { id: 'week', name: 'Week' },
        { id: 'day', name: 'Day' },
      ]}
      {...props}
    />
  );
}
